import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaUser,
  FaHistory,
  FaQuestionCircle,
  FaSignOutAlt,
  FaTimes,
  FaEdgeLegacy,
  FaRedRiver,
  FaRecycle,
  FaTrash,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { userToken } from '../redux/actions';
import { get } from '../service/api';
import logo from '../assets/images/logo_bg.png';

const MenuItem = React.memo(
  ({ title, onPress, color = '#ffffff', icon, isActive }) => (
    <button
      onClick={onPress}
      className={`flex items-center w-11/12 py-3 px-4 gap-3 rounded-lg transition-colors duration-200 ${
        isActive
          ? 'bg-yellow-500 text-black'
          : 'hover:bg-gray-700 text-gray-100'
      } focus:outline-none focus:ring-2 focus:ring-yellow-400`}
      aria-label={title}
    >
      {React.cloneElement(icon, {
        className: `w-6 h-6 ${isActive ? 'text-black' : 'text-gray-300'}`,
      })}
      <span
        className={`text-base font-medium ${isActive ? 'font-bold' : ''}`}
        style={{ color: isActive ? '#000000' : color }}
      >
        {title}
      </span>
    </button>
  ),
);

const BasicSidebarMenu = ({ isOpen = true, closeSidebar }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [messageDetail, setMessageDetail] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const userDetail = useSelector(
    (state) => state.userDetailReducer?.userDetail || {},
  );

  useEffect(() => {
    getMessageDetails();
  }, []);

  const getMessageDetails = async () => {
    try {
      setIsLoading(true);
      const data = await get('/messages/getMessageList');
      setMessageDetail(data?.response || {});
    } catch (err) {
      console.error('Error fetching messages:', err);
      toast.error('Failed to load message details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      dispatch(userToken(null));
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const showLogoutConfirmation = useCallback(() => {
    if (window.confirm('Are you sure you want to logout?')) {
      handleLogout();
    }
  }, []);

  const onClickHelp = async () => {
    console.log(messageDetail);
    if (!messageDetail?.whatsapp_number) {
      toast.error('Help service not available');
      return;
    }
    try {
      const phoneNumber =
        `+91${messageDetail?.whatsapp_number}` || '+919000000000';
      const message = messageDetail?.notice_msg || '';
      const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
        message,
      )}`;
      window.open(whatsappURL, '_blank');
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
      toast.error('Error opening WhatsApp');
    }
  };

  const menuItems = [
    {
      title: 'Profile',
      onPress: () => {
        navigate('/profile');
        closeSidebar();
      },
      icon: <FaUser />,
      path: '/profile',
    },
    {
      title: 'Order History',
      onPress: () => {
        navigate('/order-history');
        closeSidebar();
      },
      icon: <FaHistory />,
      path: '/order-history',
    },
    {
      title: 'Help',
      onPress: onClickHelp,
      icon: <FaQuestionCircle />,
      path: null, // Help doesn't have a route
    },
    {
      title: 'Logout',
      onPress: showLogoutConfirmation,
      icon: <FaSignOutAlt />,
      color: '#FF5252',
      path: null,
    },
    {
      title: 'Delete My Account',
      onPress: () => {
        navigate('/delete-account');
        closeSidebar();
      },
      icon: <FaTrash />,
      color: '#FF5252',
      path: 'delete-account',
    },
  ];

  return (
    <div
      className={`fixed top-0 left-0 h-full w-4/5 max-w-sm bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 shadow-2xl transform ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-in-out z-50 font-sans`}
      role="navigation"
      aria-hidden={!isOpen}
    >
      <div className="flex flex-col h-full overflow-y-auto">
        {/* Header with Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <img src={logo} alt="Logo" className="w-16 h-16 object-contain" />
          <button
            onClick={closeSidebar}
            className="text-gray-300 hover:text-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded-full p-2"
            aria-label="Close sidebar"
          >
            <FaTimes className="w-6 h-6" />
          </button>
        </div>

        {/* User Info */}
        <div className="flex flex-col items-center gap-2 p-4">
          <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center">
            <span className="text-2xl text-yellow-400 font-bold">
              {userDetail?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="text-center">
            <p className="text-white text-lg font-bold truncate max-w-[200px]">
              {userDetail?.name || 'User'}
            </p>
            <p className="text-gray-400 text-sm font-medium truncate max-w-[200px]">
              {userDetail?.phone_number || 'N/A'}
            </p>
          </div>
        </div>

        {/* Menu Items */}
        <div className="flex-1 px-3 py-6">
          {isLoading ? (
            <div className="flex justify-center">
              <svg
                className="animate-spin h-8 w-8 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"
                />
              </svg>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {menuItems.map((item, index) => (
                <MenuItem
                  key={index}
                  title={item.title}
                  onPress={item.onPress}
                  color={item.color}
                  icon={item.icon}
                  isActive={item.path === location.pathname}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(BasicSidebarMenu);
